import type { ReactNode } from "react";

import type { BoxProps } from "@braintrust/braintrust-ui-components";
import {
  Box,
  Chip,
  Divider,
  IconButton,
  List,
  Stack,
  Tooltip,
  Typography,
} from "@braintrust/braintrust-ui-components";
import {
  CheckIcon,
  CloseIcon,
  CopyIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { useCopyToClipboard } from "@js/hooks/copy-to-clipboard";
import type { ResumeSmartMatching } from "@js/types/jobs";
import { floatToPercent } from "@js/utils/numbers";

import { getResumeMatchMeta } from "./helpers";

type ResumeMatchSummaryProps = {
  resumeSummary: ResumeSmartMatching;
  jobRoleName: string;
};

export const ResumeMatchSummary = ({
  resumeSummary,
  jobRoleName,
}: ResumeMatchSummaryProps) => {
  const { copied, handleCopy } = useCopyToClipboard();
  return (
    <>
      <Stack sx={{ alignItems: "start", gap: 2, mb: 1.5, px: "var(--px)" }}>
        <Stack direction="row" sx={{ gap: 0.75, alignItems: "center" }}>
          <Typography component="h4" variant="label" size="medium">
            Resume Match
          </Typography>
          <ResumeMatchChip
            matchScore={resumeSummary.match_score}
            jobRoleName={jobRoleName}
          />
        </Stack>
        <Stack sx={{ gap: 2 }}>
          {!!resumeSummary.strengths.length && (
            <Box>
              <Stack
                direction="row"
                sx={{ gap: 0.25, alignItems: "center", mb: 1 }}
              >
                <Typography
                  component="h4"
                  variant="label"
                  size="medium"
                  sx={{ fontSize: "14px !important" }}
                >
                  Key Strengths
                </Typography>
                <Box sx={{ position: "relative" }}>
                  <Tooltip title={copied ? "Copied!" : "Copy Strenghts"}>
                    <IconButton
                      sx={{
                        position: "absolute",
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                      variant="transparent"
                      aria-label="Copy Strenghts to Clipboard"
                      size="x-small"
                      onClick={() => {
                        handleCopy(
                          resumeSummary.strengths
                            .map((strength) => `• ${strength.text}`)
                            .join("\n"),
                        );
                      }}
                    >
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Stack>
              <MatchSummaryList
                summary={resumeSummary.strengths}
                summaryType="positive"
              />
            </Box>
          )}
          {!!resumeSummary.gaps.length && (
            <Box>
              <Typography
                component="h4"
                variant="label"
                size="medium"
                sx={{ mb: 1, fontSize: "14px !important" }}
              >
                Gaps
              </Typography>
              <MatchSummaryList
                summary={resumeSummary.gaps}
                summaryType="negative"
              />
            </Box>
          )}
        </Stack>
      </Stack>
      <Divider color="beige" sx={{ mb: 1.5 }} />
    </>
  );
};

export const MatchSummaryList = ({
  summary,
  summaryType,
}: {
  summary: Array<{ text: string; confidence: number }>;
  summaryType: "positive" | "negative";
}) => {
  if (!summary.length) {
    return null;
  }

  return (
    <List
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        p: 0,
        overflowWrap: "anywhere",
      }}
    >
      {summary.map((item, index) => {
        return (
          <SummaryItem
            // eslint-disable-next-line react/no-array-index-key
            key={item.text + index}
            {...item}
            summaryType={summaryType}
          />
        );
      })}
    </List>
  );
};

const commonIconStyles = { marginTop: "2px", fontSize: "20px" };

type SummaryItemProps = {
  text: string;
  summaryType: "positive" | "negative";
  confidence: number;
};

const SummaryItem = ({ text, summaryType, confidence }: SummaryItemProps) => {
  return (
    <Stack component="li" direction="row" sx={{ gap: 1 }}>
      {summaryType === "positive" ? (
        <CheckIcon sx={{ color: "var(--positive-2)", ...commonIconStyles }} />
      ) : (
        <CloseIcon sx={{ color: "var(--negative-1)", ...commonIconStyles }} />
      )}
      <Typography component="div" variant="paragraph" size="small">
        {text}
        {confidence > 0.75 && (
          <Chip
            label="High confidence"
            color="beige"
            variant="squared"
            size="small"
            sx={{
              p: "4px !important",
              alignSelf: "center",
              flexShrink: 0,
              height: "min-content",
              ml: 0.75,
            }}
            LabelProps={{
              sx: {
                fontSize: "10px !important",
                color: "var(--grey-1)",
                lineHeight: "1 !important",
              },
            }}
          />
        )}
      </Typography>
    </Stack>
  );
};

type ResumeMatchChipProps = {
  matchScore: number;
  label?: ReactNode;
  labelProps?: Pick<BoxProps, "sx">;
  jobRoleName: string;
};

export const ResumeMatchChip = ({
  matchScore,
  label,
  labelProps,
  jobRoleName,
}: ResumeMatchChipProps) => {
  const { badgeStyles } = getResumeMatchMeta({
    matchScore,
    jobRoleName,
  });
  const { backgroundColor, color, borderColor } = badgeStyles;
  return (
    <Chip
      sx={{
        backgroundColor: `${backgroundColor} !important`,
        color: `${color} !important`,
        cursor: "pointer",
        "&:hover, &:focus": {
          boxShadow: `0 0 0 4px ${borderColor}`,
        },
      }}
      LabelProps={{
        sx: {
          fontSize: "14px !important",
          lineHeight: "22px !important",
          ...labelProps?.sx,
        },
      }}
      label={
        <>
          {label}
          {floatToPercent(matchScore)}
        </>
      }
      size="x-small"
    />
  );
};
